import React from 'react';
import { Helmet } from 'react-helmet';
import { motion } from 'framer-motion';
import { FaLightbulb, FaRobot, FaTools, FaMicrochip } from 'react-icons/fa';

const StudentInnovationPage = () => {
  // Student innovations data
  const innovations = [
    {
      id: 1,
      title: "Automated Plant Watering System",
      student: "Arjun Silva",
      age: 15,
      description: "A smart system that monitors soil moisture and automatically waters plants when needed. It uses soil moisture sensors and a water pump controlled by a microcontroller.",
      category: "Environmental",
      icon: <FaLightbulb className="text-green-500 text-4xl" />
    },
    {
      id: 2,
      title: "Line Following Robot",
      student: "Sameera Perera",
      age: 14,
      description: "A robot that can follow a black line on a white surface using infrared sensors. The robot is programmed to adjust its motors based on sensor readings to stay on the line.",
      category: "Robotics",
      icon: <FaRobot className="text-blue-500 text-4xl" />
    },
    {
      id: 3,
      title: "Solar Powered LED Lamp",
      student: "Dinuki Fernando",
      age: 16,
      description: "An energy-efficient lamp powered by a small solar panel. The project includes a battery for energy storage and LED lights for illumination.",
      category: "Energy",
      icon: <FaLightbulb className="text-yellow-500 text-4xl" />
    },
    {
      id: 4,
      title: "Smart Home Model",
      student: "Kaveen Jayasinghe",
      age: 15,
      description: "A miniature model of a smart home featuring automated lights, a working garage door, and temperature sensors. The system is controlled by a microcontroller.",
      category: "Home Automation",
      icon: <FaTools className="text-red-500 text-4xl" />
    },
    {
      id: 5,
      title: "Electronic Music Box",
      student: "Rashmi Wickramasinghe",
      age: 13,
      description: "A digital music box that plays different melodies based on user input. The project uses a speaker, buttons, and a microcontroller to create and play tunes.",
      category: "Entertainment",
      icon: <FaMicrochip className="text-purple-500 text-4xl" />
    },
    {
      id: 6,
      title: "Voice Controlled Robot",
      student: "Nimal Bandara",
      age: 17,
      description: "A robot that responds to voice commands through a smartphone app. The robot can move, turn, and perform simple tasks based on the commands received.",
      category: "Robotics",
      icon: <FaRobot className="text-blue-500 text-4xl" />
    }
  ];

  return (
    <>
      <Helmet>
        <title>Student Innovations | STEM Labs</title>
        <meta name="description" content="Discover innovative projects created by STEM Labs students showcasing their creativity and technical skills" />
      </Helmet>
      
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-blue-800 to-blue-600 py-20">
        <div className="container mx-auto px-4">
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-6 text-center">
            Student Innovation Platform
          </h1>
          <p className="text-white text-xl max-w-3xl mx-auto text-center">
            Showcasing the creative projects and innovations from our talented STEM Labs students
          </p>
        </div>
      </div>
      
      {/* Introduction Section */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl font-bold mb-6 text-blue-800">Nurturing Young Innovators</h2>
            <p className="text-gray-700 mb-8 text-lg">
              At STEM Labs, we believe in learning by creating. Our students develop practical skills by working on real projects
              that solve problems and demonstrate their understanding of STEM concepts. This platform showcases some of the
              outstanding innovations our students have created during their learning journey with us.
            </p>
            <div className="flex justify-center">
              <a 
                href="https://makers.stemlabs.edu.lk"
                target="_blank"
                rel="noopener noreferrer"
                className="bg-blue-600 text-white py-3 px-8 rounded-full hover:bg-blue-700 transition-colors"
              >
                Visit Full Makers Platform
              </a>
            </div>
          </div>
        </div>
      </section>
      
      {/* Innovations Gallery */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold mb-12 text-center">Featured Student Projects</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {innovations.map((innovation, index) => (
              <motion.div
                key={innovation.id}
                className="bg-white rounded-lg shadow-md overflow-hidden border border-gray-200 hover:border-blue-500 transition-all"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <div className="p-6">
                  <div className="flex items-center mb-4">
                    {innovation.icon}
                    <span className="ml-3 bg-gray-100 text-gray-800 text-xs font-medium px-2.5 py-0.5 rounded">
                      {innovation.category}
                    </span>
                  </div>
                  
                  <h3 className="text-xl font-bold mb-2 text-blue-800">{innovation.title}</h3>
                  
                  <div className="text-sm text-gray-600 mb-4">
                    By {innovation.student}, Age {innovation.age}
                  </div>
                  
                  <p className="text-gray-700 mb-4">
                    {innovation.description}
                  </p>
                  
                  <div className="pt-4 border-t border-gray-100">
                    <button className="text-blue-600 hover:text-blue-800 transition-colors font-medium">
                      Learn more
                    </button>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
      
      {/* CTA Section */}
      <section className="py-16 bg-gray-900 text-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl font-bold mb-6">Ready to Build Your Own Innovation?</h2>
            <p className="text-gray-300 mb-8">
              Join our STEM courses and workshops to develop the skills needed to bring your creative ideas to life. 
              Our experienced instructors will guide you through the process of designing, building, and programming your own projects.
            </p>
            <div className="flex flex-col sm:flex-row justify-center gap-4">
              <a 
                href="https://docs.google.com/forms/d/e/1FAIpQLSfcR5SGMKi9XrdgFROEUukUL5oINfiQQy-cnezcsjt0KbSHOQ/viewform"
                target="_blank"
                rel="noopener noreferrer"
                className="bg-red-600 text-white py-3 px-8 rounded-full hover:bg-red-700 transition-colors"
              >
                Apply for Courses
              </a>
              <a 
                href="https://docs.google.com/forms/d/e/1FAIpQLSfP6lqznlJRU_mQCkkUrxNPNMNRVuJThBLgX93pKc3TXjawwQ/viewform"
                target="_blank"
                rel="noopener noreferrer"
                className="bg-white text-blue-800 py-3 px-8 rounded-full hover:bg-gray-100 transition-colors"
              >
                Join Competitions
              </a>
            </div>
          </div>
        </div>
      </section>
      
      {/* Testimonials Section */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold mb-12 text-center">What Our Students Say</h2>
          
          <div className="max-w-5xl mx-auto">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="bg-white p-6 rounded-lg shadow-md border-l-4 border-blue-600">
                <p className="text-gray-700 mb-4 italic">
                  "STEM Labs helped me turn my ideas into reality. I built my first robot here, and now I'm working on more complex projects. The mentors are very supportive and encouraging."
                </p>
                <div className="font-semibold text-blue-800">Kavindi Rajapaksa, Age 16</div>
              </div>
              
              <div className="bg-white p-6 rounded-lg shadow-md border-l-4 border-green-600">
                <p className="text-gray-700 mb-4 italic">
                  "I never thought I could build something that actually works! The hands-on approach at STEM Labs made learning fun and helped me understand complex concepts through practical applications."
                </p>
                <div className="font-semibold text-green-700">Ashen Fernando, Age 14</div>
              </div>
              
              <div className="bg-white p-6 rounded-lg shadow-md border-l-4 border-purple-600">
                <p className="text-gray-700 mb-4 italic">
                  "The competitions and innovation challenges pushed me to think outside the box. I've learned so much more than just programming and electronics – I've learned how to solve real problems."
                </p>
                <div className="font-semibold text-purple-700">Malini Perera, Age 15</div>
              </div>
              
              <div className="bg-white p-6 rounded-lg shadow-md border-l-4 border-red-600">
                <p className="text-gray-700 mb-4 italic">
                  "My experience at STEM Labs has sparked my interest in engineering. The projects we work on are challenging but achievable, and it's so satisfying to see your creation come to life."
                </p>
                <div className="font-semibold text-red-700">Naveen Gunawardena, Age 17</div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default StudentInnovationPage; 